import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { useFeature } from "@/context/SiteModeContext";
import { themeOf } from "@/lib/mall";

/** One line per shop, read like the key on the directory board. */
function StoreRow({ card, onPick, i }) {
    const t = themeOf(card);
    return (
        <motion.button initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }}
            onClick={() => onPick(card)} data-testid={`picker-store-${card.slug}`}
            className="group flex w-full items-baseline gap-2.5 border-b border-zinc-100 py-2.5 text-left last:border-0">
            <span className="h-2.5 w-2.5 shrink-0 translate-y-[-1px] rounded-sm transition-transform duration-150 group-hover:scale-[1.4]"
                style={{ background: t.primary }} />
            <span className="shrink-0 font-display text-sm font-bold text-zinc-800 transition-colors group-hover:text-zinc-900">
                {card.name}
            </span>
            {card.city && <span className="hidden shrink-0 text-xs text-zinc-400 sm:inline">{card.city}</span>}
            <span className="min-w-[12px] flex-1 translate-y-[-4px] border-b border-dotted border-zinc-200" />
            {card.tagline && (
                <span className="hidden max-w-[16rem] shrink truncate text-xs text-zinc-500 md:inline"
                    data-testid={`picker-specialty-${card.slug}`}>
                    {card.tagline}
                </span>
            )}
            <ArrowRight size={13} className="shrink-0 text-zinc-300 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-zinc-900" />
        </motion.button>
    );
}

/** The mall asks before it lets you in: whose shop are you walking into? */
export default function StorePicker({ open, title, room, stores = [], onPick, onClose }) {
    const directoryOn = useFeature("directory");
    useEffect(() => {
        if (!open) return;
        const esc = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", esc);
        return () => window.removeEventListener("keydown", esc);
    }, [open, onClose]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
                    data-testid="store-picker"
                    className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center sm:p-6">
                    <motion.div onClick={(e) => e.stopPropagation()}
                        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 320, damping: 30 }}
                        className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-t-2xl border border-zinc-200 bg-white shadow-[0_30px_90px_-20px_rgba(0,0,0,0.5)] sm:rounded-2xl">
                        <div className="flex items-start gap-4 px-6 pb-3 pt-5">
                            <div className="min-w-0 flex-1">
                                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-zinc-400">
                                    Store key{room ? ` · ${room}` : ""}
                                </p>
                                <h2 className="mt-1.5 font-display text-lg font-extrabold tracking-tight text-zinc-900"
                                    data-testid="store-picker-title">
                                    {title || "Which shop are you walking into?"}
                                </h2>
                            </div>
                            <button onClick={onClose} data-testid="store-picker-close"
                                className="shrink-0 rounded-full p-1.5 text-zinc-400 transition-colors hover:text-zinc-900">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="border-t border-zinc-900/90 px-6">
                            {stores.map((c, i) => <StoreRow key={c.slug} card={c} onPick={onPick} i={i} />)}
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3 px-6 pb-5 pt-4">
                            <p className="text-xs text-zinc-500">
                                You stay inside the shop you pick until you head back to the directory.
                            </p>
                            {directoryOn && <Link to="/directory" onClick={onClose} data-testid="store-picker-directory"
                                className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors hover:text-zinc-900">
                                Full directory <ArrowRight size={11} />
                            </Link>}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
